import { KeyboardEvent, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TuneIcon from '@mui/icons-material/Tune';
import Dropdown from '@/components/ui/Dropdown';
import type { CalendarMode } from '@/helpers/calendarMode';
import type { TableDensity } from './density';
import DensityToggle from './DensityToggle';
import CalendarModeToggle from './CalendarModeToggle';
import styles from './ViewOptionsMenu.module.scss';

interface ViewOptionsMenuProps {
  density?: { value: TableDensity; onChange: (density: TableDensity) => void };
  calendar?: { value: CalendarMode; onChange: (mode: CalendarMode) => void };
}

/**
 * «תצוגה»: צפיפות השורות ולוח השנה של התאריכים, מאחורי כפתור אחד (HAT-1492).
 *
 * שתיהן בחירות שעושים פעם אחת ושנשמרות, אבל כשתי קבוצות כפתורים קבועות הן ישבו
 * בכל ביקור בשורת המסננים וגלשו אותה לשורה נוספת. מאז שראש הלוח קפוא בזמן
 * הגלילה, כל שורה כזו באה על חשבון שורות של תנועות לאורך כל הגלילה.
 *
 * הבחירה חלה מיד והחלון נשאר פתוח, כדי שיראו את הלוח משתנה מתחתיו. נסגר בלחיצה
 * בחוץ, ב-Escape, או ביציאה ממנו ב-Tab, ואז המיקוד חוזר לכפתור.
 */
const ViewOptionsMenu = ({ density, calendar }: ViewOptionsMenuProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  // The panel sits in a portal at the end of the body, so Tab from the button would skip it.
  useEffect(() => {
    if (!isOpen) return;
    const frame = requestAnimationFrame(() => {
      panelRef.current?.querySelector<HTMLElement>('[role="radio"][tabindex="0"]')?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [isOpen]);

  const close = () => {
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  const handlePanelKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }
    if (event.key !== 'Tab') return;
    const stops = [...(panelRef.current?.querySelectorAll<HTMLElement>('[role="radio"][tabindex="0"]') ?? [])];
    const leavingForward = !event.shiftKey && document.activeElement === stops[stops.length - 1];
    const leavingBack = event.shiftKey && document.activeElement === stops[0];
    if (leavingForward || leavingBack) {
      event.preventDefault();
      close();
    }
  };

  const trigger = (
    <button
      ref={triggerRef}
      type="button"
      className={`${styles.trigger} body-sm-regular`}
      aria-haspopup="dialog"
      aria-expanded={isOpen}
      aria-controls={isOpen ? panelId : undefined}
      data-testid="view-options"
      onKeyDown={(event) => {
        if (event.key === 'Escape' && isOpen) close();
      }}
    >
      <TuneIcon className={styles.icon} fontSize="small" aria-hidden="true" />
      <span>{t('tableViewOptions')}</span>
    </button>
  );

  return (
    <Dropdown trigger={trigger} isOpen={isOpen} setIsOpen={setIsOpen} contentAlign="end" activeTriggerClassName={styles.triggerOpen}>
      <div ref={panelRef} id={panelId} role="dialog" aria-label={t('tableViewOptions')} className={styles.panel} onKeyDown={handlePanelKeyDown}>
        {density && <DensityToggle value={density.value} onChange={density.onChange} showOnMobile />}
        {calendar && (
          <div>
            <span className={`${styles.label} body-md-regular`}>{t('calendarMode')}:</span>
            <CalendarModeToggle value={calendar.value} onChange={calendar.onChange} showOnMobile />
          </div>
        )}
      </div>
    </Dropdown>
  );
};

export default ViewOptionsMenu;
